import {
  CalendarDays,
  ChartColumn,
  Grid3x3,
  ListChecks,
  LucideIcon,
  ShieldCheck,
  SlidersHorizontal,
  Tags,
  Users,
} from 'lucide-react';
import { TitleEntry } from '../../../navigation/titleEntry';
import {
  LURKER_CAMERA_DAYS_PATH,
  LURKER_DASHBOARD_PATH,
  LURKER_LABELING_PATH,
  LURKER_LABELING_STATS_PATH,
  LURKER_QA_PATH,
  LURKER_RELABEL_PATH,
  LURKER_STRATEGIES_PATH,
  LURKER_WORK_PATH,
} from '../../paths';

/**
 * The one list of Lurker destinations, in sidebar sections. The sidebar renders it, the
 * mobile topbar takes its label from it, and the browser tab is named from it.
 */
export interface LurkerNavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  /** Shown to Lurker owners only; everyone else labels from the Labeling tool. */
  ownerOnly?: boolean;
}

/** A titled group of the sidebar. */
export interface LurkerNavSection {
  title: string;
  items: LurkerNavItem[];
}

/** OOS tasks judge the store's out-of-stock tasks; OOS model labels the model's crops. */
export const LURKER_NAV_SECTIONS: LurkerNavSection[] = [
  {
    title: 'OOS tasks',
    items: [
      { label: 'Dashboard', icon: ChartColumn, path: LURKER_DASHBOARD_PATH },
      { label: 'Camera-days', icon: CalendarDays, path: LURKER_CAMERA_DAYS_PATH },
      { label: 'Strategies', icon: SlidersHorizontal, path: LURKER_STRATEGIES_PATH },
    ],
  },
  {
    title: 'OOS model',
    items: [
      { label: 'Labeling tool', icon: Tags, path: LURKER_WORK_PATH },
      { label: 'Data explorer', icon: Grid3x3, path: LURKER_LABELING_PATH, ownerOnly: true },
      { label: 'Relabel', icon: ListChecks, path: LURKER_RELABEL_PATH, ownerOnly: true },
      { label: 'Labeller QA', icon: ShieldCheck, path: LURKER_QA_PATH, ownerOnly: true },
      { label: 'Labeling stats', icon: Users, path: LURKER_LABELING_STATS_PATH, ownerOnly: true },
    ],
  },
];

export const LURKER_NAV_ITEMS: LurkerNavItem[] = LURKER_NAV_SECTIONS.flatMap(
  (section) => section.items
);

export const LURKER_APP_TITLE = 'Lurker';

/** The nav item that owns a path; a camera-day belongs to Camera-days. */
export const lurkerNavItemFor = (pathname: string): LurkerNavItem | undefined =>
  LURKER_NAV_ITEMS.find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`));

/** Top bar title on mobile: the label of the page that owns the path, else 'Lurker'. */
export const getLurkerPageLabel = (pathname: string): string =>
  lurkerNavItemFor(pathname)?.label ?? LURKER_APP_TITLE;

/** Browser tab names for Lurker. */
export const LURKER_TITLE_ENTRIES: TitleEntry[] = [
  ...LURKER_NAV_ITEMS.map((item) => ({ path: item.path, label: item.label })),
  {
    path: `${LURKER_CAMERA_DAYS_PATH}/:cameraDayId/timelines`,
    label: 'Timelines',
    params: [{ name: 'cameraDayId' }],
  },
  {
    path: `${LURKER_CAMERA_DAYS_PATH}/:cameraDayId/images`,
    label: 'Images',
    params: [{ name: 'cameraDayId' }],
  },
  {
    path: `${LURKER_LABELING_PATH}/:batchId`,
    label: 'Batch',
    params: [{ name: 'batchId', opaque: true }],
  },
  {
    path: `${LURKER_WORK_PATH}/:batchId`,
    label: 'Batch',
    params: [{ name: 'batchId', opaque: true }],
  },
  {
    path: `${LURKER_RELABEL_PATH}/:chainId`,
    label: 'Relabel queue',
    params: [{ name: 'chainId' }],
  },
];
